import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { t } from '@lingui/macro'
import { FieldArray, Form, Formik, FormikHelpers } from 'formik'
import * as yup from 'yup'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import LoadingButton from '@mui/lab/LoadingButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import DeleteIcon from '@mui/icons-material/Delete'

import SpecialitiesAutoCompleteContainer from '@/components/formik/fields/SpecialitiesAutocompleteField'
import TextField from '@/components/formik/fields/TextField'
import CheckboxField from '@/components/formik/fields/CheckboxField'
import ImmediateConsultationConfigForm, { PaymentReceiverStaff } from './ImmediateConsultationConfigForm'

const schema = yup.object({
  busyEnabled: yup.boolean(),
  description: yup.string(),
  name: yup.string().required(),
  specialities: yup
    .array()
    .of(
      yup.object({
        code: yup.string().required(),
        deletable: yup.boolean(),
        name: yup.string().required(),
        price: yup.number().required(),
      })
    )
    .required(),
  immediateConsultationConfig: yup.object({
    enabled: yup.boolean().required(),
    maxWaitingPatients: yup.number().nullable(),
    requirePaymentUpfront: yup.boolean().required(),
    price: yup.number().nullable().when('requirePaymentUpfront', {
      is: true,
      then: (schema) => schema.min(0, t`Price must be positive`),
    }),
    paymentReceiver: yup.string().nullable().when('requirePaymentUpfront', {
      is: true,
      then: (schema) => schema.required(t`Payment receiver is required when payment is enabled`),
    }),
  }),
  waitingRoom: yup.string().nullable().when('immediateConsultationConfig.enabled', {
    is: true,
    then: (schema) => schema.required(t`Waiting room is required`),
  }),
})

export type MeetingLinkInfoFormValues = yup.InferType<typeof schema>

export type MeetingLinkInfoProps = {
  meetingLinkInfo: MeetingLinkInfoFormValues
  onSubmit: (values: MeetingLinkInfoFormValues) => void
  editable: boolean
  setEditable: (editable: boolean) => void
  staffs: PaymentReceiverStaff[]
}

export function MeetingLinkInfo(props: MeetingLinkInfoProps) {
  const { meetingLinkInfo, onSubmit, editable, setEditable, staffs } = props
  const { t } = useTranslation()

  const [deleteSpecialityAlertDialogOpen, setDeleteSpecialityAlertDialogOpen] = useState(false)

  const toggleEditable = () => {
    setEditable(!editable)
  }

  const toggleDeleteSpecialityAlertDialog = () => {
    setDeleteSpecialityAlertDialogOpen((prevOpen) => !prevOpen)
  }

  return (
    <>
      <Formik
        initialValues={meetingLinkInfo}
        validationSchema={schema}
        enableReinitialize
        onSubmit={(values: MeetingLinkInfoFormValues, actions: FormikHelpers<MeetingLinkInfoFormValues>) => {
          onSubmit(values)
          actions.setSubmitting(false)
        }}
      >
        {({ values, handleSubmit, isValid, resetForm, errors }) => (
          <Form onSubmit={handleSubmit}>
            <Paper sx={{ p: 3 }}>
              <Container maxWidth="md">
                <Grid container spacing={3}>
                  <Grid container item spacing={4}>
                    <Grid item xs={12}>
                      <Grid container item xs={8}>
                        <TextField readOnly={!editable} fullWidth label={t('field.name.label')} name="name" />
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField readOnly={!editable} fullWidth label={t('field.description.label')} multiline name="description" rows={3} />
                    </Grid>
                    <Grid item xs={12}>
                      <CheckboxField readOnly={!editable} label={t('field.busyEnabled.label')} name="busyEnabled" />
                    </Grid>
                    <Grid item xs={12}>
                      <SpecialitiesAutoCompleteContainer
                        id="speciality"
                        name="specialities"
                        label={t('field.speciality.label', { context: 'plural' })}
                        editable={editable}
                        canDelete={false}
                      />
                      <Stack divider={<Divider />} spacing={3} mt={2}>
                        <FieldArray
                          name="specialities"
                          render={(arrayHelpers) => {
                            return values.specialities.map((speciality, index) => (
                              <Stack key={`${speciality.name}-${index}`} direction="row" alignItems="center" justifyContent="space-between">
                                <Box>{speciality.name}</Box>
                                <Stack direction="row" spacing={1}>
                                  <TextField
                                    InputProps={{ endAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                    name={`specialities.${index}.price`}
                                    placeholder={t('field.price.label')}
                                    readOnly={!editable}
                                    type="number"
                                    error={!!errors?.specialities?.[index]}
                                  />
                                  {editable && (
                                    <IconButton
                                      onClick={() => {
                                        if (speciality.deletable) {
                                          arrayHelpers.remove(index)
                                        } else {
                                          toggleDeleteSpecialityAlertDialog()
                                        }
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </Stack>
                              </Stack>
                            ))
                          }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <ImmediateConsultationConfigForm readOnly={!editable} staffs={staffs} />
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2} marginTop={2}>
                      {!editable ? (
                        <Button onClick={toggleEditable} variant="contained">
                          {t('common:button.modify.label')}
                        </Button>
                      ) : (
                        <>
                          <LoadingButton color="primary" disabled={!isValid} disableElevation type="submit" variant="contained">
                            {t('common:button.submit.label')}
                          </LoadingButton>
                          <Button
                            onClick={() => {
                              resetForm()
                              toggleEditable()
                            }}
                            variant="neutral"
                          >
                            {t('common:button.cancel.label')}
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Container>
            </Paper>
          </Form>
        )}
      </Formik>
      <Dialog open={deleteSpecialityAlertDialogOpen} onClose={toggleDeleteSpecialityAlertDialog}>
        <DialogTitle>{t('page.meetingLink.meetingLinkInfo.deleteSpecialityAlertDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('page.meetingLink.meetingLinkInfo.deleteSpecialityAlertDialog.content')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={toggleDeleteSpecialityAlertDialog}>{t('common:button.close.label')}</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default MeetingLinkInfo
